import assert from 'assert';

import type {FieldDefinitionNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FieldDefinitionType,
  LinkDefinitionOrInaccessibleInfoNode,
  TagSchemaItemType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {assertNever} from 'util/Types';

type FieldDefinitionTypename = FieldDefinitionNode['__typename'];

export interface FieldDefinitionForValidation {
  __typename: FieldDefinitionTypename;
  type: FieldDefinitionType;
  isMulti?: boolean;
}

// Centralized mapping between schema `FieldType` and GraphQL `FieldDefinitionType`.
export const FIELD_TYPE_TO_FIELD_DEFINITION_TYPE: Readonly<Record<FieldType, FieldDefinitionType>> = {
  [FieldType.TEXT]: FieldDefinitionType.TEXT,
  [FieldType.LONG_TEXT]: FieldDefinitionType.LONG_TEXT,
  [FieldType.INTEGER]: FieldDefinitionType.INTEGER,
  [FieldType.FLOAT]: FieldDefinitionType.FLOAT,
  [FieldType.BOOLEAN]: FieldDefinitionType.BOOLEAN,
  [FieldType.DATE]: FieldDefinitionType.DATE,
  [FieldType.DATETIME]: FieldDefinitionType.DATETIME,
  [FieldType.JSON]: FieldDefinitionType.JSON,
  [FieldType.SELECTOR]: FieldDefinitionType.DROPDOWN,
  [FieldType.AA_PART_LINK]: FieldDefinitionType.AA_PART_LINK,
  [FieldType.ASSAY_REQUEST_LINK]: FieldDefinitionType.ASSAY_REQUEST_LINK,
  [FieldType.ASSAY_RESULT_LINK]: FieldDefinitionType.ASSAY_RESULT_LINK,
  [FieldType.ASSAY_RUN_LINK]: FieldDefinitionType.ASSAY_RUN_LINK,
  [FieldType.BATCH_LINK]: FieldDefinitionType.BATCH_LINK,
  [FieldType.BLOB_LINK]: FieldDefinitionType.BLOB_LINK,
  [FieldType.ENTRY_LINK]: FieldDefinitionType.ENTRY_LINK,
  [FieldType.BIOENTITY_LINK]: FieldDefinitionType.ENTITY_LINK,
  [FieldType.PART_LINK]: FieldDefinitionType.PART_LINK,
  [FieldType.SAMPLE_LINK]: FieldDefinitionType.SAMPLE_LINK,
  [FieldType.STORABLE_LINK]: FieldDefinitionType.STORAGE_LINK,
  [FieldType.TRANSLATION_LINK]: FieldDefinitionType.TRANSLATION_LINK,
  [FieldType.TRANSCRIPTION_LINK]: FieldDefinitionType.TRANSCRIPTION_LINK,
  [FieldType.BASE_MOLECULE_LINK]: FieldDefinitionType.BASE_MOLECULE_LINK,
};

export function fieldTypeToFieldDefinitionType(fieldType: FieldType): FieldDefinitionType {
  const fieldDefinitionType = FIELD_TYPE_TO_FIELD_DEFINITION_TYPE[fieldType];
  assert(fieldDefinitionType != null, `Unsupported field type: ${fieldType}`);
  return fieldDefinitionType;
}

interface TagSchemaNode {
  __typename: 'TagSchemaNode';
  id: string;
  itemType: TagSchemaItemType;
  folderItemType: string;
}

/** Interface reference for schema link fields that link to a schema interface. */
interface SchemaInterfaceNodeForValidation {
  __typename: 'SchemaInterfaceNode';
  id: string;
  itemType?: TagSchemaItemType | null;
  /** IDs of schemas that implement this interface */
  schemas?: ReadonlyArray<{id: string}>;
}

type LinkDefinitionSchemaInterfaceNode = Extract<
  LinkDefinitionOrInaccessibleInfoNode,
  {__typename: 'SchemaInterfaceNode'}
>;

interface HasSchema {
  schema:
    | TagSchemaNode
    | {__typename: 'InaccessibleInfoNode'}
    | {__typename: Exclude<string, 'TagSchemaNode' | 'InaccessibleInfoNode'>; id: string}
    | null;
  /** Set when the field links to a custom or static schema interface (fieldset/system category). */
  interface?: SchemaInterfaceNodeForValidation | {__typename: 'InaccessibleInfoNode'} | null;
}

interface HasLinkDefinition {
  linkDefinition: LinkDefinitionOrInaccessibleInfoNode | null;
}

export type DropdownLinkFieldDefinitionForValidation = FieldDefinitionForValidation & HasLinkDefinition;
export type SchemaLinkFieldDefinitionForValidation = FieldDefinitionForValidation & HasSchema;
export type LinkDefinitionFieldDefinitionForValidation = FieldDefinitionForValidation & HasLinkDefinition;

// Keep in sync with the backend `fieldset_item_type_to_field_definition_type` in
// benchling/schemas/core/utils/field_definition_type_utils.py: the frontend and backend must resolve a
// fieldset to the same category.
export function tagSchemaItemTypeToFieldDefinitionType(itemType: TagSchemaItemType): FieldDefinitionType {
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return FieldDefinitionType.AA_SEQUENCE_LINK;
    case TagSchemaItemType.DNA_SEQUENCE:
    case TagSchemaItemType.DNA_OLIGO:
    case TagSchemaItemType.MIXED:
      return FieldDefinitionType.DNA_SEQUENCE_LINK;
    case TagSchemaItemType.RNA_OLIGO:
    case TagSchemaItemType.RNA_SEQUENCE:
      return FieldDefinitionType.RNA_SEQUENCE_LINK;
    case TagSchemaItemType.ENTRY:
      return FieldDefinitionType.ENTRY_LINK;
    case TagSchemaItemType.MIXTURE:
      return FieldDefinitionType.MIXTURE_LINK;
    case TagSchemaItemType.MOLECULE:
      return FieldDefinitionType.MOLECULE_LINK;
    case TagSchemaItemType.BASIC_FOLDER_ITEM:
    case TagSchemaItemType.ADC:
    case TagSchemaItemType.ANTIBODY:
    case TagSchemaItemType.EQUIPMENT:
    case TagSchemaItemType.OLIGO_CONJUGATE:
    case TagSchemaItemType.OLIGO_DUPLEX:
    case TagSchemaItemType.TRIAL_SUBJECT:
      return FieldDefinitionType.CUSTOM_ENTITY_LINK;
    default:
      assertNever(itemType);
      assert(false, `Unknown tag schema item type: ${itemType}`);
  }
}

const SpecificEntityFieldDefinitionType = {
  ANTIBODY_LINK: 'ANTIBODY_LINK',
  EQUIPMENT_LINK: 'EQUIPMENT_LINK',
  OLIGO_CONJUGATE_LINK: 'OLIGO_CONJUGATE_LINK',
  OLIGO_DUPLEX_LINK: 'OLIGO_DUPLEX_LINK',
} as const;

type SpecificEntityFieldDefinitionType =
  (typeof SpecificEntityFieldDefinitionType)[keyof typeof SpecificEntityFieldDefinitionType];

type EffectiveFieldDefinitionType = FieldDefinitionType | SpecificEntityFieldDefinitionType;

const SPECIFIC_ENTITY_FIELD_DEFINITION_TYPES: ReadonlyArray<SpecificEntityFieldDefinitionType> = [
  SpecificEntityFieldDefinitionType.ANTIBODY_LINK,
  SpecificEntityFieldDefinitionType.EQUIPMENT_LINK,
  SpecificEntityFieldDefinitionType.OLIGO_CONJUGATE_LINK,
  SpecificEntityFieldDefinitionType.OLIGO_DUPLEX_LINK,
];

function tagSchemaItemTypeToEffectiveFieldDefinitionType(
  itemType: TagSchemaItemType
): EffectiveFieldDefinitionType {
  switch (itemType) {
    case TagSchemaItemType.ANTIBODY:
      return SpecificEntityFieldDefinitionType.ANTIBODY_LINK;
    case TagSchemaItemType.EQUIPMENT:
      return SpecificEntityFieldDefinitionType.EQUIPMENT_LINK;
    case TagSchemaItemType.OLIGO_CONJUGATE:
      return SpecificEntityFieldDefinitionType.OLIGO_CONJUGATE_LINK;
    case TagSchemaItemType.OLIGO_DUPLEX:
      return SpecificEntityFieldDefinitionType.OLIGO_DUPLEX_LINK;
    default:
      return tagSchemaItemTypeToFieldDefinitionType(itemType);
  }
}

// Exhaustive map: TypeScript will error if any FieldDefinitionType is missing.
export const FIELD_TYPE_SUBTYPES: Readonly<Record<FieldDefinitionType, ReadonlyArray<FieldDefinitionType>>> =
  {
    // Types with subtypes
    [FieldDefinitionType.ENTITY_LINK]: [
      FieldDefinitionType.AA_PART_LINK,
      FieldDefinitionType.AA_SEQUENCE_LINK,
      FieldDefinitionType.CUSTOM_ENTITY_LINK,
      FieldDefinitionType.DNA_SEQUENCE_LINK,
      FieldDefinitionType.MIXTURE_LINK,
      FieldDefinitionType.MOLECULE_LINK,
      FieldDefinitionType.PART_LINK,
      FieldDefinitionType.RNA_SEQUENCE_LINK,
      FieldDefinitionType.TRANSCRIPTION_LINK,
      FieldDefinitionType.TRANSLATION_LINK,
    ],
    [FieldDefinitionType.LONG_TEXT]: [FieldDefinitionType.TEXT],
    [FieldDefinitionType.TEXT]: [FieldDefinitionType.LONG_TEXT],
    // Types without subtypes
    [FieldDefinitionType.AA_PART_LINK]: [],
    [FieldDefinitionType.AA_SEQUENCE_LINK]: [],
    [FieldDefinitionType.ASSAY_REQUEST_LINK]: [],
    [FieldDefinitionType.ASSAY_RESULT_LINK]: [],
    [FieldDefinitionType.ASSAY_RUN_LINK]: [],
    [FieldDefinitionType.BATCH_LINK]: [],
    [FieldDefinitionType.BLOB_LINK]: [],
    [FieldDefinitionType.BOOLEAN]: [],
    [FieldDefinitionType.CUSTOM_ENTITY_LINK]: [],
    [FieldDefinitionType.DATE]: [],
    [FieldDefinitionType.DATETIME]: [],
    [FieldDefinitionType.DNA_SEQUENCE_LINK]: [],
    [FieldDefinitionType.DROPDOWN]: [],
    [FieldDefinitionType.ENTRY_LINK]: [],
    [FieldDefinitionType.FLOAT]: [],
    [FieldDefinitionType.INTEGER]: [],
    [FieldDefinitionType.JSON]: [],
    [FieldDefinitionType.MIXTURE_LINK]: [],
    [FieldDefinitionType.MOLECULE_LINK]: [],
    [FieldDefinitionType.PART_LINK]: [],
    [FieldDefinitionType.RNA_PART_LINK]: [],
    [FieldDefinitionType.RNA_SEQUENCE_LINK]: [],
    [FieldDefinitionType.SAMPLE_LINK]: [],
    [FieldDefinitionType.STORAGE_LINK]: [],
    [FieldDefinitionType.TRANSCRIPTION_LINK]: [],
    [FieldDefinitionType.TRANSLATION_LINK]: [],
    [FieldDefinitionType.BASE_MOLECULE_LINK]: [],
  };

/**
 * Maps GraphQL FieldDefinitionType to client-side FieldType.
 *
 * Note: This is a lossy (many-to-one) mapping. Multiple FieldDefinitionType values
 * map to a single FieldType (e.g., CUSTOM_ENTITY_LINK, AA_SEQUENCE_LINK, DNA_SEQUENCE_LINK,
 * ENTITY_LINK, MIXTURE_LINK, and MOLECULE_LINK all map to BIOENTITY_LINK).
 */
const FIELD_DEFINITION_TYPE_TO_FIELD_TYPE: Readonly<Record<FieldDefinitionType, FieldType>> = {
  [FieldDefinitionType.TEXT]: FieldType.TEXT,
  [FieldDefinitionType.LONG_TEXT]: FieldType.LONG_TEXT,
  [FieldDefinitionType.INTEGER]: FieldType.INTEGER,
  [FieldDefinitionType.FLOAT]: FieldType.FLOAT,
  [FieldDefinitionType.DATE]: FieldType.DATE,
  [FieldDefinitionType.DATETIME]: FieldType.DATETIME,
  [FieldDefinitionType.AA_PART_LINK]: FieldType.AA_PART_LINK,
  [FieldDefinitionType.ASSAY_REQUEST_LINK]: FieldType.ASSAY_REQUEST_LINK,
  [FieldDefinitionType.ASSAY_RESULT_LINK]: FieldType.ASSAY_RESULT_LINK,
  [FieldDefinitionType.ASSAY_RUN_LINK]: FieldType.ASSAY_RUN_LINK,
  [FieldDefinitionType.BATCH_LINK]: FieldType.BATCH_LINK,
  [FieldDefinitionType.BLOB_LINK]: FieldType.BLOB_LINK,
  [FieldDefinitionType.BOOLEAN]: FieldType.BOOLEAN,
  [FieldDefinitionType.CUSTOM_ENTITY_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.AA_SEQUENCE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.DNA_SEQUENCE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.ENTITY_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.DROPDOWN]: FieldType.SELECTOR,
  [FieldDefinitionType.ENTRY_LINK]: FieldType.ENTRY_LINK,
  [FieldDefinitionType.JSON]: FieldType.JSON,
  [FieldDefinitionType.MIXTURE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.MOLECULE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.PART_LINK]: FieldType.PART_LINK,
  [FieldDefinitionType.RNA_PART_LINK]: FieldType.PART_LINK,
  [FieldDefinitionType.RNA_SEQUENCE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.SAMPLE_LINK]: FieldType.SAMPLE_LINK,
  [FieldDefinitionType.STORAGE_LINK]: FieldType.STORABLE_LINK,
  [FieldDefinitionType.TRANSLATION_LINK]: FieldType.TRANSLATION_LINK,
  [FieldDefinitionType.TRANSCRIPTION_LINK]: FieldType.TRANSCRIPTION_LINK,
  [FieldDefinitionType.BASE_MOLECULE_LINK]: FieldType.BASE_MOLECULE_LINK,
};

const FieldDefinitionTypenameResolution = {
  USE_FIELD_TYPE_PROPERTY: 'USE_FIELD_TYPE_PROPERTY',
  SCHEMA_LINK: 'SCHEMA_LINK',
  SCHEMA_INTERFACE_LINK: 'SCHEMA_INTERFACE_LINK',
} as const;

type FieldDefinitionTypenameResolution =
  (typeof FieldDefinitionTypenameResolution)[keyof typeof FieldDefinitionTypenameResolution];

type EffectiveFieldDefinitionTypeResolution =
  | EffectiveFieldDefinitionType
  | FieldDefinitionTypenameResolution;

const T = FieldDefinitionType;
const S = SpecificEntityFieldDefinitionType;
const R = FieldDefinitionTypenameResolution;

// Exhaustively classifies each GraphQL field definition node by how to resolve its effective
// type for source-to-target compatibility checks. Most nodes have a static effective type;
// schema-backed nodes need runtime link metadata to determine their effective entity type.
const FIELD_DEFINITION_TYPENAME_RESOLUTION: Readonly<
  Record<FieldDefinitionTypename, EffectiveFieldDefinitionTypeResolution>
> = {
  AaPartLinkFieldDefinitionNode: T.AA_PART_LINK,
  AaSequenceLinkFieldDefinitionNode: T.AA_SEQUENCE_LINK,
  AntibodyLinkFieldDefinitionNode: S.ANTIBODY_LINK,
  AnyEntityLinkFieldDefinitionNode: T.ENTITY_LINK,
  AssayRequestLinkFieldDefinitionNode: T.ASSAY_REQUEST_LINK,
  AssayResultLinkFieldDefinitionNode: T.ASSAY_RESULT_LINK,
  AssayRunLinkFieldDefinitionNode: T.ASSAY_RUN_LINK,
  BaseFieldDefinitionNode: R.USE_FIELD_TYPE_PROPERTY,
  BaseMoleculeLinkFieldDefinitionNode: T.BASE_MOLECULE_LINK,
  BlobLinkFieldDefinitionNode: T.BLOB_LINK,
  BooleanFieldDefinitionNode: T.BOOLEAN,
  CustomEntityLinkFieldDefinitionNode: T.CUSTOM_ENTITY_LINK,
  DateFieldDefinitionNode: T.DATE,
  DateTimeFieldDefinitionNode: T.DATETIME,
  DnaOligoLinkFieldDefinitionNode: T.DNA_SEQUENCE_LINK,
  DnaPartLinkFieldDefinitionNode: T.PART_LINK,
  DnaSequenceLinkFieldDefinitionNode: T.DNA_SEQUENCE_LINK,
  DropdownLinkFieldDefinitionNode: T.DROPDOWN,
  EntryLinkFieldDefinitionNode: T.ENTRY_LINK,
  EquipmentLinkFieldDefinitionNode: S.EQUIPMENT_LINK,
  FieldsetLinkFieldDefinitionNode: R.SCHEMA_INTERFACE_LINK,
  FloatFieldDefinitionNode: T.FLOAT,
  IntegerFieldDefinitionNode: T.INTEGER,
  JsonFieldDefinitionNode: T.JSON,
  LongTextFieldDefinitionNode: T.LONG_TEXT,
  MixtureLinkFieldDefinitionNode: T.MIXTURE_LINK,
  MoleculeLinkFieldDefinitionNode: T.MOLECULE_LINK,
  OligoConjugateLinkFieldDefinitionNode: S.OLIGO_CONJUGATE_LINK,
  OligoDuplexLinkFieldDefinitionNode: S.OLIGO_DUPLEX_LINK,
  RnaOligoLinkFieldDefinitionNode: T.RNA_SEQUENCE_LINK,
  RnaPartLinkFieldDefinitionNode: T.RNA_PART_LINK,
  RnaSequenceLinkFieldDefinitionNode: T.RNA_SEQUENCE_LINK,
  SchemaLinkFieldDefinitionNode: R.SCHEMA_LINK,
  StorableLinkFieldDefinitionNode: T.STORAGE_LINK,
  SystemCategoryLinkFieldDefinitionNode: R.SCHEMA_INTERFACE_LINK,
  TextFieldDefinitionNode: T.TEXT,
  TranscriptionLinkFieldDefinitionNode: T.TRANSCRIPTION_LINK,
  TranslationLinkFieldDefinitionNode: T.TRANSLATION_LINK,
};

export function fieldDefinitionTypeToFieldType(fieldDefinitionType: FieldDefinitionType): FieldType {
  return FIELD_DEFINITION_TYPE_TO_FIELD_TYPE[fieldDefinitionType];
}

/**
 * Checks if sourceType is compatible with targetType.
 * Compatible means either equal or sourceType is a subtype of targetType.
 */
function isTypeOrSubtype(
  sourceType: EffectiveFieldDefinitionType,
  targetType: EffectiveFieldDefinitionType
): boolean {
  if (sourceType === targetType) {
    return true;
  }
  if (targetType === FieldDefinitionType.ENTITY_LINK) {
    return (
      SPECIFIC_ENTITY_FIELD_DEFINITION_TYPES.includes(sourceType as SpecificEntityFieldDefinitionType) ||
      (FIELD_TYPE_SUBTYPES[targetType]?.includes(sourceType as FieldDefinitionType) ?? false)
    );
  }
  if (targetType === FieldDefinitionType.CUSTOM_ENTITY_LINK) {
    return SPECIFIC_ENTITY_FIELD_DEFINITION_TYPES.includes(sourceType as SpecificEntityFieldDefinitionType);
  }
  if (SPECIFIC_ENTITY_FIELD_DEFINITION_TYPES.includes(targetType as SpecificEntityFieldDefinitionType)) {
    return false;
  }
  return (
    FIELD_TYPE_SUBTYPES[targetType as FieldDefinitionType]?.includes(sourceType as FieldDefinitionType) ??
    false
  );
}

function resolveTypeFromSchemaLink(
  field: SchemaLinkFieldDefinitionForValidation
): EffectiveFieldDefinitionType {
  // For schema link field definitions we use ENTITY_LINK if it's "Any entity" or a specific schema link. Otherwise
  // we use the category of item as the type i.e. Any DNA Sequence is DNA_SEQUENCE_LINK. Therefore, when there is a
  // schema link we check the itemType of the schema to determine compatibility.
  const schema = field.schema;
  if (schema != null && !isInaccessible(schema) && isTagSchemaNode(schema)) {
    return tagSchemaItemTypeToEffectiveFieldDefinitionType(schema.itemType);
  }
  return field.type;
}

// Fieldset and system category link fields are stored as schema interface links. When
// the interface declares an entity item type, use that as the field's effective type.
function resolveTypeFromSchemaInterfaceLink(
  field: LinkDefinitionFieldDefinitionForValidation
): EffectiveFieldDefinitionType {
  const linkDefinition = field.linkDefinition;
  if (
    linkDefinition != null &&
    !isInaccessible(linkDefinition) &&
    isSchemaInterfaceNode(linkDefinition) &&
    linkDefinition.itemType != null
  ) {
    return tagSchemaItemTypeToEffectiveFieldDefinitionType(linkDefinition.itemType);
  }
  return field.type;
}

// Normalizes concrete field definition nodes into the type used for compatibility.
// This lets callers compare source and target types without special-casing each node pair.
function resolveEffectiveFieldDefinitionType(
  field: FieldDefinitionForValidation
): EffectiveFieldDefinitionType {
  const resolution = FIELD_DEFINITION_TYPENAME_RESOLUTION[field.__typename];
  switch (resolution) {
    case FieldDefinitionTypenameResolution.USE_FIELD_TYPE_PROPERTY:
      return field.type;
    case FieldDefinitionTypenameResolution.SCHEMA_LINK:
      return isSchemaLinkFieldDefinition(field) ? resolveTypeFromSchemaLink(field) : field.type;
    case FieldDefinitionTypenameResolution.SCHEMA_INTERFACE_LINK:
      return hasLinkDefinitionProperty(field) ? resolveTypeFromSchemaInterfaceLink(field) : field.type;
    default:
      return resolution;
  }
}

export function areTypesCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): boolean {
  return isTypeOrSubtype(
    resolveEffectiveFieldDefinitionType(sourceField),
    resolveEffectiveFieldDefinitionType(targetField)
  );
}

export function areCardinalitiesCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): boolean {
  if ((sourceField.isMulti ?? false) && !(targetField.isMulti ?? false)) {
    return false;
  }
  return true;
}

function isDropdownLinkFieldDefinition(
  field: FieldDefinitionForValidation
): field is DropdownLinkFieldDefinitionForValidation {
  return field.__typename === 'DropdownLinkFieldDefinitionNode';
}

export function isAnyDropdownFieldDefinition(
  field: FieldDefinitionForValidation
): field is DropdownLinkFieldDefinitionForValidation {
  return isDropdownLinkFieldDefinition(field);
}

function isSchemaLinkFieldDefinition(
  field: FieldDefinitionForValidation
): field is SchemaLinkFieldDefinitionForValidation {
  return field.__typename === 'SchemaLinkFieldDefinitionNode';
}

function hasLinkDefinitionProperty(
  field: FieldDefinitionForValidation
): field is LinkDefinitionFieldDefinitionForValidation {
  return field.hasOwnProperty('linkDefinition');
}

function isTagSchemaNode(
  schema: NonNullable<SchemaLinkFieldDefinitionForValidation['schema']>
): schema is TagSchemaNode {
  return schema.__typename === 'TagSchemaNode';
}

function isSchemaInterfaceNode(
  linkDefinition: NonNullable<LinkDefinitionFieldDefinitionForValidation['linkDefinition']>
): linkDefinition is LinkDefinitionSchemaInterfaceNode {
  return linkDefinition.__typename === 'SchemaInterfaceNode';
}

function getDropdownId(field: DropdownLinkFieldDefinitionForValidation): string | null {
  if (field.linkDefinition != null && !isInaccessible(field.linkDefinition)) {
    return field.linkDefinition.id;
  }
  return null;
}

export function areDropdownsCompatible(
  sourceField: DropdownLinkFieldDefinitionForValidation,
  targetField: DropdownLinkFieldDefinitionForValidation
): boolean {
  return getDropdownId(sourceField) === getDropdownId(targetField);
}

function getSchemaId(field: SchemaLinkFieldDefinitionForValidation): string | null {
  if (field.schema == null || isInaccessible(field.schema)) {
    return null;
  }
  return field.schema.id;
}

function getInterfaceId(field: SchemaLinkFieldDefinitionForValidation): string | null {
  const schemaInterface = field.interface;
  if (schemaInterface == null || isInaccessible(schemaInterface)) {
    return null;
  }
  return schemaInterface.id;
}

/** Returns IDs of schemas that implement the field's interface. */
function getImplementingSchemaIds(field: SchemaLinkFieldDefinitionForValidation): ReadonlyArray<string> {
  const schemaInterface = field.interface;
  if (schemaInterface == null || isInaccessible(schemaInterface)) {
    return [];
  }
  const schemas = schemaInterface.schemas;
  if (schemas == null) {
    return [];
  }
  return schemas.map((s) => s.id);
}

/**
 * When target is a schema interface field, source must either
 * link to the same interface or have a schema that implements that interface.
 */
export function areInterfacesCompatible(
  sourceField: SchemaLinkFieldDefinitionForValidation,
  targetField: SchemaLinkFieldDefinitionForValidation
): boolean {
  const targetInterfaceId = getInterfaceId(targetField);
  if (targetInterfaceId == null) {
    return true;
  }
  const sourceInterfaceId = getInterfaceId(sourceField);
  if (sourceInterfaceId === targetInterfaceId) {
    return true;
  }
  const sourceSchemaId = getSchemaId(sourceField);
  if (sourceSchemaId != null) {
    const implementingIds = getImplementingSchemaIds(targetField);
    return implementingIds.length > 0 && implementingIds.includes(sourceSchemaId);
  }
  return false;
}

function getLinkDefinitionId(field: LinkDefinitionFieldDefinitionForValidation): string | null {
  if (field.linkDefinition == null || isInaccessible(field.linkDefinition)) {
    return null;
  }
  return field.linkDefinition.id;
}

export function areLinkSchemasCompatible(
  sourceField: SchemaLinkFieldDefinitionForValidation,
  targetField: SchemaLinkFieldDefinitionForValidation
): boolean {
  const targetSchemaId = getSchemaId(targetField);
  if (targetSchemaId == null) {
    return true;
  }
  return getSchemaId(sourceField) === targetSchemaId;
}

export function areLinkDefinitionsCompatible(
  sourceField: LinkDefinitionFieldDefinitionForValidation,
  targetField: LinkDefinitionFieldDefinitionForValidation
): boolean {
  const targetLinkDefId = getLinkDefinitionId(targetField);
  if (targetLinkDefId == null) {
    return true;
  }
  return getLinkDefinitionId(sourceField) === targetLinkDefId;
}

export interface FieldCompatibilityResult {
  isCompatible: boolean;
  incompatibilityReason: string | null;
}

export function areFieldDefinitionsCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): FieldCompatibilityResult {
  if (!areTypesCompatible(sourceField, targetField)) {
    return {
      isCompatible: false,
      incompatibilityReason: "Field definitions don't match: type mismatch.",
    };
  }

  if (!areCardinalitiesCompatible(sourceField, targetField)) {
    const sourceCardinality = sourceField.isMulti ? 'multiple values' : 'single value';
    const targetCardinality = targetField.isMulti ? 'multiple values' : 'single value';
    return {
      isCompatible: false,
      incompatibilityReason: `Field definitions don't match: source expects ${sourceCardinality}, target expects ${targetCardinality}.`,
    };
  }

  if (isAnyDropdownFieldDefinition(sourceField) && isAnyDropdownFieldDefinition(targetField)) {
    if (!areDropdownsCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: dropdown selector mismatch.",
      };
    }
  }

  // SchemaLinkFieldDefinition is slated to be deprecated, but for now we want to maintain compatibility checks for them
  if (isSchemaLinkFieldDefinition(sourceField) && isSchemaLinkFieldDefinition(targetField)) {
    if (!areLinkSchemasCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: schema mismatch.",
      };
    }
    if (getInterfaceId(targetField) != null && !areInterfacesCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason:
          "Field definitions don't match: source must link to the same interface or to a schema that implements the target's interface.",
      };
    }
  }

  // TODO(BNCH-143948): Add additional check for interface fields (Fieldset and SystemCategory) that verifies
  // that the specific schema satisfies the interface or that the link definitions for the interface match
  const targetIsInterface =
    targetField.__typename === 'FieldsetLinkFieldDefinitionNode' ||
    targetField.__typename === 'SystemCategoryLinkFieldDefinitionNode';
  if (
    hasLinkDefinitionProperty(sourceField) &&
    hasLinkDefinitionProperty(targetField) &&
    !targetIsInterface
  ) {
    if (!areLinkDefinitionsCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: link definition mismatch.",
      };
    }
  }

  return {isCompatible: true, incompatibilityReason: null};
}
